import { Outlet, useLocation } from "react-router-dom";
import { useEffect, useRef, useState } from "react";

import { Header } from "../../../modules/header/presentation/components/header";
import { Sidebar } from "../../../modules/sidebar/presentation/components/sidebar";

import { useMe, useMePartner } from "../../../swr";
import type { AppRoute } from "../../dashboard/authenticated_app";
import Footer from "../../../modules/footer/presentation/components/footer";

import { useLogout } from "../../../modules/authentication/hooks/useLogout";
import { Role } from "../../../api";
import { BuyerTopNav } from "./buyer-top-nav";
import { ApprovalBanner } from "../../dashboard/approval-banner";
import { RouteErrorBoundary } from "../error-boundary";

export type AppShellProps = {
  navigation: AppRoute[];
};

export function AppShell({ navigation }: AppShellProps) {
  const { user, mutate } = useMe();
  const { isBuyer } = useMePartner();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const handleLogout = useLogout();
  const mainRef = useRef<HTMLElement>(null);
  const { pathname } = useLocation();

  useEffect(() => {
    mainRef.current?.scrollTo({ top: 0, left: 0 });
  }, [pathname]);

  const showBuyerTopNav = isBuyer && user && user.role !== Role.Admin;

  if (showBuyerTopNav) {
    return (
      <div className="flex h-screen flex-col overflow-hidden print:h-auto print:overflow-visible">
        <ApprovalBanner />
        <BuyerTopNav user={user} onLogout={handleLogout} />
        <main
          ref={mainRef}
          className="flex flex-1 flex-col overflow-auto overscroll-none print:flex-none print:overflow-visible"
          id="main-container"
        >
          <div className="flex flex-grow flex-col text-textPrimary">
            <RouteErrorBoundary resetKey={pathname}>
              <Outlet />
            </RouteErrorBoundary>
          </div>
          <Footer />
        </main>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col overflow-hidden print:h-auto print:overflow-visible">
      <ApprovalBanner />
      <div className="flex flex-1 flex-col lg:flex-row overflow-hidden print:flex-none print:overflow-visible">
      <Sidebar
        navigation={navigation}
        open={sidebarOpen}
        setOpen={setSidebarOpen}
        username={user?.name || ""}
        userId={String(user?.id || "")}
      />

      <div className="flex flex-col flex-1 min-w-0 h-full print:h-auto">
        <Header
          user={user!}
          mutate={mutate}
          setSidebarOpen={setSidebarOpen}
          handleLogout={handleLogout}
        />

        <main
          ref={mainRef}
          className="flex flex-col flex-1 min-h-0 overflow-auto overscroll-none print:flex-none print:h-auto print:overflow-visible"
          id="main-container"
        >
          <div className="text-textPrimary flex flex-col flex-grow min-w-0">
            <RouteErrorBoundary resetKey={pathname}>
              <Outlet />
            </RouteErrorBoundary>
          </div>

          <Footer />
        </main>
      </div>
      </div>
    </div>
  );
}
